import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import {
  Close, MenuBook, OpenInNew,
  ShoppingCart, VideoLibrary, Public, Link as LinkIcon, Info,
  VpnKey, Lock, AddCircle, SettingsInputComponent, CheckCircle,
  Sync, Security, SyncDisabled, Settings, TaskAlt, AutoFixHigh as MagicWand,
  ChevronRight
} from '@mui/icons-material';

type ConnectionStatus = 'idle' | 'testing' | 'syncing' | 'success';

export const BindStoreScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showGuide, setShowGuide] = useState(false);

  // Form State
  const [storeName, setStoreName] = useState('');
  const [clientId, setClientId] = useState('');
  const [apiKey, setApiKey] = useState('');

  // Process State
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [syncProgress, setSyncProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);

  const handleTestConnection = () => {
    setStatus('testing');
    setTimeout(() => {
      setStatus('syncing');
    }, 1500);
  };

  useEffect(() => {
    if (status === 'syncing') {
      const interval = setInterval(() => {
        setSyncProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStatus('success');
            setShowToast(true);
            return 100;
          }
          return prev + 5;
        });
      }, 150);
      return () => clearInterval(interval);
    }
  }, [status]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header
        breadcrumbs={[
          { label: t('workspace.nav'), path: '/workspace' },
          { label: t('storeBind.title') }
        ]}
      />

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-8 right-8 z-[100] w-96 bg-white border border-slate-200 rounded-xl p-4 shadow-xl flex gap-4 animate-in slide-in-from-right-10 duration-500">
          <div className="flex-shrink-0 w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="text-emerald-500" fontSize="small" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-slate-900">{t('storeBind.toastTitle')}</h4>
            <p className="text-xs text-slate-500 mt-1">{t('storeBind.toastDesc')}</p>
          </div>
          <button onClick={() => setShowToast(false)} className="text-slate-400 hover:text-slate-600 transition-colors active:scale-95">
            <Close fontSize="small" />
          </button>
        </div>
      )}

      {/* Help Drawer Overlay */}
      {showGuide && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] animate-in fade-in duration-300"
          onClick={() => setShowGuide(false)}
        />
      )}

      {/* Help Drawer */}
      <aside className={`fixed top-0 right-0 h-full w-[420px] bg-white z-[70] shadow-2xl transition-transform duration-300 border-l border-slate-200 overflow-y-auto ${showGuide ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <MenuBook className="text-primary" />
              {t('storeBind.guideTitle')}
            </h3>
            <button
              onClick={() => setShowGuide(false)}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors active:scale-95"
            >
              <Close />
            </button>
          </div>
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                <h4 className="font-bold text-slate-900">{t('storeBind.step1Title')}</h4>
              </div>
              <div className="ml-9 space-y-4">
                <p className="text-sm text-slate-600">{t('storeBind.step1Desc')}</p>
                <a className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-lg text-xs font-bold shadow-md shadow-primary/20 hover:bg-blue-700 transition-all group" href="https://seller.ozon.ru/" target="_blank" rel="noreferrer">
                  {t('storeBind.step1Btn')}
                  <OpenInNew className="text-[16px] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" fontSize="small" />
                </a>
                <div className="rounded-lg border border-slate-200 bg-slate-50 h-32 flex items-center justify-center overflow-hidden">
                  <img alt="Ozon Login" className="w-full h-full object-cover opacity-80" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBDvgq7tb_oJTl3JVkxKdsuJbTlXzGJUQEKozToJuR750EmNAHlYlS4AIlvCNZIMfMwGOrk9CMo1DxodtGP96JRZbr1yBmK-Z9emA6pCrnmImzgEL7fQv7GV-tu2OAT07dpDLMJF8ZLj_WtVQpi_6n6_9S__xzgTsHixrGCuAav8A4rY-fV3boibxRhWxNd5Lulq0zp0At_EBOzt8sbjRP2ZPj57U4dnrZfaHGT7xbv-dnrXiyPcd5KKSAWFOSYyk-IEIwVffHl0krs" />
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                <h4 className="font-bold text-slate-900">{t('storeBind.step2Title')}</h4>
              </div>
              <div className="ml-9 space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-slate-600">{t('storeBind.step2Desc')}</p>
                  <a className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-colors" href="https://seller.ozon.ru/app/settings/api-keys" target="_blank" rel="noreferrer">
                    {t('storeBind.step2Btn')}
                    <OpenInNew className="text-[14px]" style={{ fontSize: 14 }} />
                  </a>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 h-32 flex items-center justify-center overflow-hidden">
                  <img alt="Ozon Settings" className="w-full h-full object-cover opacity-80" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBGDLE1BqUfPOOnOGR_Kv00G9JTUIFFfI6mO_Gl2LiM5ETQUSLUvW_uTwVV-iqCJeb-1nmMEw4yOhtuyUcjATzx73-FD3Dly0RoDVCbsT4SR7UciE2tC5MZLIPYhvDWzkaEIi10eCVHTDESWWHKW_tVQ7mUQvZel0i95pwor1MS_nN1G8mRwJfyWi6cw5yuPdUMYBTaG2AbaluJlM4QV9xjrA1oGRrV_Ub6jVg8hTRK085Xs8f-kckgf1Jo7OdU1cdGeeblJDgX9AU6" />
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                <h4 className="font-bold text-slate-900">{t('storeBind.step3Title')}</h4>
              </div>
              <div className="ml-9 space-y-4">
                <p className="text-sm text-slate-600">{t('storeBind.step3Desc')}</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 h-32 flex items-center justify-center overflow-hidden">
                  <img alt="Generate API Key" className="w-full h-full object-cover opacity-80" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDi3F7_FK15kl1zbLdbZZrlTHoPPIEmN-jONnQRCI6n0cM6PC7CJ7iJUNXb6qtAN7vuRSImGXJtdm9Ji4D5HwZ3FnAegKW_F8OrWm_HyAxilqxyv-6AyP9JTQLnmYGNLmWKu7WETrkJAZ8cKXUgCNeyIhuAUfXKVpGpHCII4PqoQJEwHLhPcTXUkusYm0ZjguLz8VdIC1krkukFB-yNrHNG8n0IjHYqCmnXAvT35FZlYYn4a7dGFE20zTwbWJNvgNNcUQJ4qA_mG4qq" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="max-w-6xl mx-auto px-6 py-12 pb-24">
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('storeBind.title')}</h1>
          <p className="text-slate-500 text-lg">{t('storeBind.subtitle')}</p>
        </div>

        {/* Store Switcher (Visible on Success) */}
        {status === 'success' && (
          <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em]">{t('storeBind.activeSwitcher')}</h3>
              <a className="text-[11px] font-bold text-primary hover:underline uppercase tracking-wider flex items-center gap-1 cursor-pointer">
                {t('storeBind.manageAll')}
                <ChevronRight style={{ fontSize: 14 }} />
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Active Card */}
              <button className="bg-white rounded-xl p-4 text-left relative transition-all group border-2 border-primary shadow-[0_4px_12px_rgba(37,99,235,0.08)] active:scale-[0.98]">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-8 h-8 bg-[#005BFF] rounded-lg flex items-center justify-center text-[10px] text-white font-bold italic">OZ</div>
                  <span className="text-[9px] font-bold bg-primary text-white px-2 py-0.5 rounded-full uppercase">{t('storeBind.currentlyActive')}</span>
                </div>
                <div>
                  <h4 className="font-bold text-sm text-slate-900">{storeName || t('storeBind.storeNamePlaceholder')}</h4>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-slate-500">Ozon Global</span>
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      <span className="text-[10px] font-medium text-emerald-600">{t('storeBind.synced')}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                    {t('storeBind.uploads7d')}: <span className="text-slate-600">0</span>
                  </span>
                  <div className="flex gap-0.5 items-end h-3">
                    <div className="w-1 bg-primary/20 h-1 rounded-t-sm"></div>
                    <div className="w-1 bg-primary/20 h-1.5 rounded-t-sm"></div>
                    <div className="w-1 bg-primary/20 h-1 rounded-t-sm"></div>
                  </div>
                </div>
              </button>

              {/* Placeholder Card */}
              <button className="bg-slate-50/50 border border-dashed border-slate-200 rounded-xl p-4 flex flex-col items-center justify-center gap-2 hover:bg-slate-100 transition-all active:scale-[0.98]">
                <AddCircle className="text-slate-400" />
                <span className="text-[11px] font-bold text-slate-500 uppercase">{t('storeBind.connectNew')}</span>
              </button>
            </div>
          </div>
        )}

        {/* Platform Selection */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border-2 border-[#005BFF] rounded-2xl p-6 shadow-lg shadow-blue-500/5 flex flex-col items-center text-center relative overflow-hidden active:scale-95 transition-transform cursor-pointer">
            <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
              <span className="text-[10px] font-bold text-blue-600 uppercase">{t('storeBind.active')}</span>
            </div>
            <div className="w-16 h-16 bg-[#005BFF] rounded-2xl flex items-center justify-center mb-4 text-white font-bold text-2xl italic">OZ</div>
            <h3 className="font-bold text-slate-900">Ozon</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">{t('storeBind.fullApiIntegration')}</p>
            <span className="text-[11px] font-semibold text-[#005BFF] uppercase tracking-wider">{t('storeBind.readyConnect')}</span>
          </div>

          {[
            { name: 'Amazon', icon: <ShoppingCart style={{ fontSize: 32 }} className="text-slate-400" /> },
            { name: 'TikTok Shop', icon: <VideoLibrary style={{ fontSize: 32 }} className="text-slate-400" /> },
            { name: 'AliExpress', icon: <Public style={{ fontSize: 32 }} className="text-slate-400" /> }
          ].map((platform, i) => (
            <div key={i} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex flex-col items-center text-center grayscale opacity-60 hover:opacity-100 transition-opacity cursor-not-allowed">
              <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mb-4">
                {platform.icon}
              </div>
              <h3 className="font-bold text-slate-900">{platform.name}</h3>
              <p className="text-xs text-slate-500 mt-1 mb-4">{t('storeBind.comingSoon')}</p>
            </div>
          ))}
        </div>

        {/* Connection Form */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-12">
          <div className="p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-[#005BFF]">
                  <LinkIcon />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{t('storeBind.connectOzon')}</h2>
                  <p className="text-sm text-slate-500">
                    {t('storeBind.statusLabel')}: <span className={`font-medium ${status === 'success' ? 'text-emerald-500' : 'text-orange-500'}`}>
                      {status === 'success' ? t('storeBind.authorized') : t('storeBind.noStoresLinked')}
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowGuide(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/20 text-primary text-xs font-bold hover:bg-primary/5 transition-all active:scale-95"
              >
                <Info fontSize="small" />
                {t('storeBind.howToGetKey')}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t('storeBind.storeName')}</label>
                    <input
                      value={storeName}
                      onChange={(e) => setStoreName(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 text-sm focus:ring-primary focus:border-primary px-4 py-2.5 outline-none transition-all"
                      placeholder={t('storeBind.storeNamePlaceholder')}
                      type="text"
                      disabled={status === 'success'}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t('storeBind.clientId')}</label>
                    <div className="relative">
                      <input
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        className="w-full pr-10 rounded-lg border border-slate-300 text-sm focus:ring-primary focus:border-primary px-4 py-2.5 outline-none transition-all"
                        placeholder={t('storeBind.clientIdPlaceholder')}
                        type="text"
                        disabled={status === 'success'}
                      />
                      <VpnKey className="absolute right-3 top-2.5 text-slate-300" style={{ fontSize: 18 }} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">{t('storeBind.apiKey')}</label>
                  <div className="relative">
                    <input
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="w-full pr-10 rounded-lg border border-slate-300 text-sm focus:ring-primary focus:border-primary px-4 py-2.5 outline-none transition-all"
                      placeholder="••••••••••••••••"
                      type="password"
                      disabled={status === 'success'}
                    />
                    <Lock className="absolute right-3 top-2.5 text-slate-300" style={{ fontSize: 18 }} />
                  </div>
                </div>

                <div className="space-y-6">
                  {status === 'idle' || status === 'testing' ? (
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        className="px-8 bg-primary text-white font-bold py-3 rounded-xl shadow-lg shadow-primary/20 hover:bg-blue-700 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={!storeName || !clientId || !apiKey}
                      >
                        <AddCircle style={{ fontSize: 18 }} />
                        {t('storeBind.addStore')}
                      </button>
                      <button
                        onClick={handleTestConnection}
                        disabled={!storeName || !clientId || !apiKey || status === 'testing'}
                        className="px-6 py-3 border border-slate-200 bg-white rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all flex items-center gap-2 group active:scale-95"
                      >
                        <SettingsInputComponent style={{ fontSize: 18 }} className={`text-primary ${status === 'testing' ? 'animate-pulse' : ''}`} />
                        {status === 'testing' ? t('storeBind.testing') : t('storeBind.testConnection')}
                      </button>
                    </div>
                  ) : status === 'syncing' ? (
                    <div className="p-5 border border-slate-200 rounded-xl bg-slate-50/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Sync className="text-primary animate-spin" style={{ fontSize: 20 }} />
                          <span className="text-sm font-semibold text-slate-900">{t('storeBind.syncingData')}</span>
                        </div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase">{syncProgress}%</span>
                      </div>
                      <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden relative">
                        <div className="absolute top-0 left-0 h-full bg-primary transition-all duration-200" style={{ width: `${syncProgress}%` }}></div>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-tight mt-3">{t('storeBind.syncingDesc')}</p>
                    </div>
                  ) : (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <div className="flex flex-wrap items-center gap-4">
                        <button className="px-8 bg-primary text-white font-bold py-3 rounded-xl shadow-lg shadow-primary/20 hover:bg-blue-700 transition-all flex items-center justify-center gap-2 active:scale-95">
                          <MagicWand style={{ fontSize: 18 }} />
                          {t('storeBind.startListing')}
                        </button>
                        <div className="flex items-center gap-2 px-6 py-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                          <CheckCircle className="text-emerald-500" style={{ fontSize: 18 }} />
                          <span className="text-xs font-bold text-emerald-600">{t('storeBind.connectionSuccess')}</span>
                        </div>
                      </div>
                      <div className="p-5 border border-slate-200 rounded-xl bg-slate-50/50">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <TaskAlt className="text-emerald-500" style={{ fontSize: 20 }} />
                            <span className="text-sm font-semibold text-slate-900">{t('storeBind.syncComplete')} (100%)</span>
                          </div>
                          <span className="text-[10px] font-bold text-emerald-500 uppercase">{t('storeBind.success')}</span>
                        </div>
                        <div className="w-full h-1 bg-emerald-500 rounded-full"></div>
                        <p className="text-[11px] text-slate-500 leading-tight mt-3">{t('storeBind.syncCompleteDesc')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Security Sidebar */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 h-fit">
                <h4 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Security className="text-primary" style={{ fontSize: 18 }} />
                  {t('storeBind.securityTitle')}
                </h4>
                <ul className="space-y-4">
                  <li className="flex gap-3">
                    <Lock className="text-primary flex-shrink-0" style={{ fontSize: 18 }} />
                    <p className="text-[12px] text-slate-600 leading-relaxed">{t('storeBind.securityEncryption')}</p>
                  </li>
                  <li className="flex gap-3">
                    <SyncDisabled className="text-primary flex-shrink-0" style={{ fontSize: 18 }} />
                    <p className="text-[12px] text-slate-600 leading-relaxed">{t('storeBind.securityNoPassword')}</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Authorized Stores Table */}
        <div className="mb-12">
          <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">{t('storeBind.authorizedStores')}</h3>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">{t('storeBind.storeName')}</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">{t('storeBind.platform')}</th>
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">{t('storeBind.statusLabel')}</th>
                  {status === 'success' && <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase">{t('storeBind.uploads7d')}</th>}
                  <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase text-right">{t('storeBind.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {status === 'success' && (
                  <tr className="hover:bg-slate-50/50 transition-colors animate-in fade-in slide-in-from-left-4 duration-500">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-900">{storeName || t('storeBind.storeNamePlaceholder')}</span>
                        <span className="text-[9px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold uppercase">{t('storeBind.active')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-[#005BFF] rounded flex items-center justify-center text-[10px] text-white font-bold italic">OZ</div>
                        <span className="text-sm">Ozon</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        <span className="text-xs font-medium text-green-600">{t('storeBind.synced')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-slate-400 font-medium">0 {t('storeBind.items')}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors active:scale-95">
                          <Settings style={{ fontSize: 20 }} />
                        </button>
                        <button className="px-3 py-1.5 rounded-lg border border-slate-300 text-[11px] font-bold hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5 active:scale-95">
                          <Sync style={{ fontSize: 14 }} />
                          {t('storeBind.syncSettings')}
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                <tr className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-semibold text-slate-900">Nordic Living Ru</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-[#005BFF] rounded flex items-center justify-center text-[10px] text-white font-bold italic">OZ</div>
                      <span className="text-sm">Ozon</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      <span className="text-xs font-medium text-green-600">{t('storeBind.connected')}</span>
                    </div>
                  </td>
                  {status === 'success' && (
                    <td className="px-6 py-4">
                      <span className="text-xs text-slate-400 font-medium">142 {t('storeBind.items')}</span>
                    </td>
                  )}
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors active:scale-95">
                        <Settings style={{ fontSize: 20 }} />
                      </button>
                      <button className="px-3 py-1.5 rounded-lg border border-slate-300 text-[11px] font-bold hover:bg-slate-50 transition-colors inline-flex items-center gap-1.5 active:scale-95">
                        <Sync style={{ fontSize: 14 }} />
                        {t('storeBind.syncSettings')}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Security Footer */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 py-8 border-t border-slate-200 mt-12">
          <div className="flex items-center gap-2">
            <TaskAlt className="text-slate-400" />
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{t('storeBind.securityGuaranteed')}</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
              <VpnKey className="text-primary" style={{ fontSize: 14 }} />
              <span className="text-[11px] font-medium text-slate-600">{t('storeBind.encryptedBridge')}</span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-200">
              <SyncDisabled className="text-primary" style={{ fontSize: 14 }} />
              <span className="text-[11px] font-medium text-slate-600">{t('storeBind.noPasswordStorage')}</span>
            </div>
          </div>
        </div>
      </main>

      <footer className="h-16 bg-white/95 backdrop-blur-md border-t border-slate-200 flex items-center px-8 justify-between fixed bottom-0 left-0 right-0 z-50">
        <div className="flex items-center gap-6">
          <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wider hidden sm:block">{t('storeBind.storageSystem')}</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/workspace')}
            className="px-5 py-2 rounded-lg border border-slate-300 font-semibold text-xs hover:bg-slate-50 transition-all active:scale-95"
          >
            {t('storeBind.cancel')}
          </button>
          <button
            disabled={status !== 'success'}
            onClick={() => navigate('/workspace')}
            className="px-6 py-2 rounded-lg bg-primary text-white font-bold text-xs shadow-lg shadow-primary/20 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {t('storeBind.saveAuth')}
          </button>
        </div>
      </footer>
    </div>
  );
};
